// useSaveTrip — the EditTripModal save flow.
//
// Extracted verbatim from `EditTripModal.js` (behavior-preserving refactor,
// issue #240). This hook owns the `isSaving` flag and the `handleSave` action,
// preserving the EXACT order and awaited semantics the 10.1 parity test pins:
//
//   1. set `isSaving` true (disables the Save button / shows the spinner),
//   2. `await onSave(editedTrip)` — the save callback is awaited FIRST,
//   3. on resolve, call `onClose()` — the modal closes ONLY after a successful
//      save (onClose is never reached if onSave rejects),
//   4. on reject, log `console.error('EditTripModal: Error saving trip:', error)`
//      and do NOT close,
//   5. in `finally`, clear `isSaving` (always re-enables the button).
//
// `onSave`/`onClose` are injected as params (they are props on the container).
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useCallback } from 'react';

/**
 * Owns the save flow: `isSaving` state and the awaited save → close handler.
 *
 * @param {Object} params
 * @param {Object} params.editedTrip - The current edited-trip working copy that
 *   is passed to `onSave`.
 * @param {(trip: Object) => (Promise<*>|*)} params.onSave - The save callback,
 *   awaited before the modal closes.
 * @param {() => void} params.onClose - Closes the modal; called only after a
 *   successful save.
 * @returns {{ isSaving: boolean, handleSave: () => Promise<void> }} The save API.
 */
export function useSaveTrip({ editedTrip, onSave, onClose }) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(editedTrip);
      onClose();
    } catch (error) {
      console.error('EditTripModal: Error saving trip:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editedTrip, onSave, onClose]);

  return { isSaving, handleSave };
}

export default useSaveTrip;
